import moment from "moment";
import React, { useState } from "react";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  Table,
} from "flowbite-react";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { CATEGORIES, LOAN } from "../../config/apiConfig";
import Drawer from "../Drawer";
import { formatToReal } from "../../utils/utils";
import {
  FaBuilding,
  FaCheck,
  FaEnvelope,
  FaPhone,
  FaRegClock,
  FaRegEye,
  FaRegTrashAlt,
} from "react-icons/fa";
import {
  FiCalendar,
  FiDollarSign,
  FiExternalLink,
  FiFileText,
  FiHash,
  FiLayers,
  FiMapPin,
} from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import TableLoading from "../TableLoading";
import CreateQueryForm from "../forms/CreateQueryForm";
import EditQueryCustomForm from "../forms/EditQueryCustomForm";
import DrawerLoading from "../DrawerLoading";
import EmptyTable from "./EmptyTable";
import ModalDelete from "../modals/ModalDelete";

const formatLoanDate = (value) =>
  value ? moment(value).format("DD/MM/YYYY") : "—";

const formatLoanAge = (value) => {
  if (!value) return "—";

  const days = moment().diff(moment(value), "days");
  if (days <= 0) return "Hoje";
  if (days === 1) return "Há 1 dia";
  return `Há ${days} dias`;
};

const getLoanCompanyName = (loan) =>
  loan?.Company?.name || "Nome não informado";

const getLoanAccessKey = (loan) => loan?.accessKey || "—";

const getLoanAmount = (loan) => formatToReal(loan?.amount);

const SimulationsTable = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const onPageChange = (page) => setCurrentPage(page);
  const [sortByPrice, setSortByPrice] = useState("");
  const [searchName, setSearchName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [minAmountFilter, setMinAmountFilter] = useState("");
  const [maxAmountFilter, setMaxAmountFilter] = useState("");
  const [fromDateFilter, setFromDateFilter] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    searchName: "",
    status: "",
    minAmount: "",
    maxAmount: "",
    fromDate: "",
    toDate: "",
  });
  const [isDrawerEditLoan, setIsDrawerEditLoan] = useState(false);
  const [isDrawerCreateSimulation, setIsDrawerCreateSimulation] =
    useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedLoanToDelete, setSelectedLoanToDelete] = useState(null);
  const [isDeletingSimulation, setIsDeletingSimulation] = useState(false);
  const [averageCredit, setAverageCredit] = useState(0);

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const rejectionOptions = [
    "Documento ilegível",
    "Documento cortado",
    "Documento fora do padrão",
    "Documento vencido",
    "Documento não corresponde ao tipo solicitado",
  ];

  const openRejectModal = (docId) => {
    setSelectedDocumentId(docId);
    setIsRejectModalOpen(true);
  };

  const confirmRejection = async () => {
    if (!rejectionReason) return alert("Selecione o motivo da rejeição.");
    try {
      await axios.post(`${LOAN.INVALIDATE_DOCUMENT}/${selectedDocumentId}`, {
        reason: rejectionReason,
      });
      await fetchLoanDetails(selectedLoan.identifier, selectedLoan.accessKey);
      setIsRejectModalOpen(false);
      setRejectionReason("");
    } catch (err) {
      console.error("Erro ao rejeitar documento:", err.message);
    }
  };

  const {
    isLoading: isLoadingLoans,
    data: loans,
  } = useQuery(
    [
      "loans",
      currentPage,
      sortByPrice,
      appliedFilters.searchName,
      appliedFilters.status,
      appliedFilters.minAmount,
      appliedFilters.maxAmount,
      appliedFilters.fromDate,
      appliedFilters.toDate,
    ],
    async () => {
      const params = new URLSearchParams();
      params.set("page", currentPage);
      if (sortByPrice) {
        params.set("sortByPrice", sortByPrice);
      }

      const addParam = (key, value) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.set(key, value);
        }
      };

      const {
        searchName: appliedSearchName,
        status,
        minAmount,
        maxAmount,
        fromDate,
        toDate,
      } = appliedFilters;

      addParam("searchName", appliedSearchName);
      addParam("status", status);
      addParam("minAmount", minAmount);
      addParam("maxAmount", maxAmount);
      addParam("fromDate", fromDate);
      addParam("toDate", toDate);

      const { data } = await axios.get(`${LOAN.GET_ALL}?${params.toString()}`);
      setTotalPages(data.data.totalPages || 1);
      setTotalCount(data.data.totalCount || 0);
      setAverageCredit(Number(data.data.averageAmount) || 0);
      return data.data.data;
    }
  );

  const { data: documentTypes } = useQuery(["documentTypes"], async () => {
    const { data } = await axios.get(`${LOAN.GET_DOCUMENT_TYPES}`);
    return data.data;
  });

  const fetchLoanDetails = async (loanIdentifier, accessKey) => {
    const { data } = await axios.get(
      `${LOAN.GET_LOAN_BY_IDENTIFIER}/${loanIdentifier}?accessKey=${accessKey}`
    );
    setSelectedLoan(data.data);
  };

  const { data: categories } = useQuery(["categories"], async () => {
    const { data } = await axios.get(`${CATEGORIES.GET_ALL}`);
    return data.data.data;
  });

  const commitFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({
      searchName: searchName.trim(),
      status: statusFilter,
      minAmount: minAmountFilter,
      maxAmount: maxAmountFilter,
      fromDate: fromDateFilter,
      toDate: toDateFilter,
    });
  };

  const applyFilters = (event) => {
    event.preventDefault();
    commitFilters();
  };

  const resetFilters = () => {
    setSearchName("");
    setStatusFilter("");
    setMinAmountFilter("");
    setMaxAmountFilter("");
    setFromDateFilter("");
    setToDateFilter("");
    setAppliedFilters({
      searchName: "",
      status: "",
      minAmount: "",
      maxAmount: "",
      fromDate: "",
      toDate: "",
    });
    setCurrentPage(1);
  };

  const handleSortChange = (value) => {
    setSortByPrice(value);
    setCurrentPage(1);
  };

  const handleViewSimulation = async (loan) => {
    await fetchLoanDetails(loan.identifier, loan.accessKey);
    setIsOpenModal(true);
  };

  const handleDeleteSimulation = (loan) => {
    setSelectedLoanToDelete(loan);
    setIsOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedLoanToDelete(null);
    setIsDeletingSimulation(false);
  };

  const confirmDeleteSimulation = async () => {
    if (!selectedLoanToDelete) return;
    try {
      setIsDeletingSimulation(true);
      await axios.delete(`${LOAN.DELETE}/${selectedLoanToDelete.id}`);
      queryClient.invalidateQueries([
        "loans",
        currentPage,
        sortByPrice,
        appliedFilters.searchName,
        appliedFilters.status,
        appliedFilters.minAmount,
        appliedFilters.maxAmount,
        appliedFilters.fromDate,
        appliedFilters.toDate,
      ]);
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Erro ao excluir a simulação:", error);
      alert("Não foi possível excluir a simulação.");
    } finally {
      setIsDeletingSimulation(false);
    }
  };

  const isUploaded = (docTypeId, partnerId = null) =>
    selectedLoan?.Documents?.some(
      (doc) =>
        doc.documentTypeId === docTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null)
    );

  const getDocsByAppliesTo = (appliesTo, partnerId = null) =>
    documentTypes
      ?.filter(
        (docType) =>
          docType.appliesTo === appliesTo || docType.appliesTo === "both"
      )
      ?.map((docType) => ({
        ...docType,
        uploaded: isUploaded(docType.id, partnerId),
      }));

  const getUploadedDoc = (docTypeId, partnerId = null) =>
    selectedLoan?.Documents?.find(
      (doc) =>
        doc.documentTypeId === docTypeId &&
        (doc.partnerId ?? null) === (partnerId ?? null)
    );

  const countDocs = (appliesTo, partnerId = null) => {
    const docs = getDocsByAppliesTo(appliesTo, partnerId) || [];
    const total = docs.length;
    const enviados = docs.filter((doc) => doc.uploaded).length;
    return `${enviados}/${total}`;
  };

  const simulationFormUrl = selectedLoan?.identifier
    ? `https://app.ampliacapital.com.br/form/${selectedLoan.identifier}`
    : null;
  const companyDocs = getDocsByAppliesTo("company") || [];
  const companyPartners = selectedLoan?.Company?.Partners || [];
  const createdAtDate = formatLoanDate(selectedLoan?.createdAt);
  const createdAtTime = selectedLoan?.createdAt
    ? moment(selectedLoan.createdAt).format("HH:mm")
    : "—";
  const requestedAmount =
    selectedLoan?.amount != null ? formatToReal(selectedLoan.amount) : "—";
  const incomeMonth =
    selectedLoan?.Company?.incomeMonth != null
      ? formatToReal(selectedLoan.Company.incomeMonth)
      : "—";

  return (
    <>
      <div className="flex flex-col gap-3 mb-3 mt-2">
        <div className="flex justify-end">
          <Button
            className="primary"
            onClick={() => setIsDrawerCreateSimulation(true)}
          >
            Criar nova simulação
          </Button>
        </div>
        <form
          className="w-full rounded-xl border border-[#d6cfc5] bg-white p-4 shadow-sm"
          onSubmit={applyFilters}
        >
          <div className="grid gap-3 text-gray-700 md:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Cliente ou responsável
              </label>
              <input
                type="text"
                value={searchName}
                onChange={(event) => setSearchName(event.target.value)}
                placeholder="Buscar por nome ou responsável"
                className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
              >
                <option value="">Todos</option>
                <option value="0">Pendente</option>
                <option value="1">Aprovado</option>
                <option value="2">Reprovado</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Ordenar por valor
              </label>
              <select
                value={sortByPrice}
                onChange={({ target }) => handleSortChange(target.value)}
                className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
              >
                <option value="">Mais recentes</option>
                <option value="DESC">Maior valor</option>
                <option value="ASC">Menor valor</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Faixa de crédito (R$)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={minAmountFilter}
                  onChange={(event) => setMinAmountFilter(event.target.value)}
                  placeholder="Mínimo"
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
                <input
                  type="number"
                  value={maxAmountFilter}
                  onChange={(event) => setMaxAmountFilter(event.target.value)}
                  placeholder="Máximo"
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-600">
                Período (criação)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={fromDateFilter}
                  onChange={(event) => setFromDateFilter(event.target.value)}
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
                <input
                  type="date"
                  value={toDateFilter}
                  onChange={(event) => setToDateFilter(event.target.value)}
                  className="w-full rounded border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-secondary focus:outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1 md:col-span-2 lg:col-span-1">
              <label className="text-[11px] uppercase tracking-[0.3em] text-transparent">
                Ações
              </label>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" type="button" onClick={commitFilters}>
                  Aplicar filtros
                </Button>
                <Button
                  size="sm"
                  color="light"
                  type="button"
                  onClick={resetFilters}
                >
                  Limpar
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className="grid gap-3 mt-3 md:grid-cols-2">
        <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <div className="h-1 w-full bg-indigo-400" />
          <div className="px-4 py-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">
                Total de simulações
              </p>
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
                <FiFileText size={13} />
              </span>
            </div>
            <p className="text-xl font-bold text-gray-900">{totalCount}</p>
            <p className="text-[10px] mt-1 text-gray-400">
              Atualizado com a página atual
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
          <div className="h-1 w-full bg-emerald-400" />
          <div className="px-4 py-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">
                Valor médio de crédito
              </p>
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500">
                <FiDollarSign size={13} />
              </span>
            </div>
            <p className="text-xl font-bold text-emerald-600">
              {formatToReal(averageCredit)}
            </p>
            <p className="text-[10px] mt-1 text-gray-400">
              Média entre as simulações carregadas
            </p>
          </div>
        </div>
      </div>

      <section className="mt-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-slate-400">
              Resultados
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">
              Simulações cadastradas
            </h2>
            <p className="text-sm text-slate-500">
              Lista paginada com filtros aplicados.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm">
              {totalCount} registros
            </span>
            {totalPages > 1 && (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-500">
                Página {currentPage} de {totalPages}
              </span>
            )}
          </div>
        </div>

        {isLoadingLoans ? (
          <>
            <div className="grid gap-3 md:hidden">
              {Array.from({ length: 4 }).map((_, index) => (
                <SimulationSkeletonCard key={index} />
              ))}
            </div>

            <div className="hidden md:block overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
              <div className="border-b border-slate-100 bg-slate-50/90 px-5 py-4">
                <div className="h-4 w-44 rounded-full bg-slate-200 animate-pulse" />
                <div className="mt-2 h-3 w-72 rounded-full bg-slate-100 animate-pulse" />
              </div>
              <Table striped={false} className="text-sm">
                <Table.Head className="sticky top-0 z-10 uppercase text-[10px] tracking-[0.24em]">
                  <Table.HeadCell className="bg-slate-50/95 text-slate-400 font-semibold">
                    ID
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50/95 text-slate-400 font-semibold">
                    Razão Social
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50/95 text-center text-slate-400 font-semibold">
                    Cod. Acesso
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50/95 text-center text-slate-400 font-semibold">
                    Data criação
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50/95 text-center text-slate-400 font-semibold">
                    Valor
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-slate-50/95 text-right text-slate-400 font-semibold">
                    Ações
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y divide-slate-100 bg-white">
                  <TableLoading cols={6} />
                </Table.Body>
              </Table>
            </div>
          </>
        ) : loans?.length > 0 ? (
          <>
            <div className="grid gap-3 md:hidden">
              {loans.map((loan) => (
                <SimulationRowCard
                  key={loan.id}
                  loan={loan}
                  onView={handleViewSimulation}
                  onDelete={handleDeleteSimulation}
                />
              ))}
            </div>

            <div className="hidden md:block overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
              <div className="border-b border-slate-100 bg-slate-50/90 px-5 py-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-slate-400">
                  Tabela de simulações
                </p>
              </div>

              <div className="overflow-x-auto">
                <Table striped={false} className="text-sm">
                  <Table.Head className="uppercase text-[10px] tracking-[0.24em]">
                    <Table.HeadCell className="bg-slate-50/95 px-5 text-slate-400 font-semibold">
                      ID
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-slate-50/95 px-4 text-slate-400 font-semibold">
                      Razão Social
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-slate-50/95 px-4 text-center text-slate-400 font-semibold">
                      Cod. Acesso
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-slate-50/95 px-4 text-center text-slate-400 font-semibold">
                      Data criação
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-slate-50/95 px-4 text-center text-slate-400 font-semibold">
                      Valor
                    </Table.HeadCell>
                    <Table.HeadCell className="bg-slate-50/95 px-5 text-right text-slate-400 font-semibold">
                      Ações
                    </Table.HeadCell>
                  </Table.Head>

                  <Table.Body className="divide-y divide-slate-100 bg-white">
                    {loans.map((loan, index) => (
                      <Table.Row
                        key={loan.id}
                        className={`group transition-colors hover:bg-slate-50/80 ${
                          index % 2 === 0 ? "bg-white" : "bg-slate-50/30"
                        }`}
                      >
                        <Table.Cell className="px-5 py-4 align-top">
                          <Badge className="w-fit rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600">
                            #{loan.id}
                          </Badge>
                        </Table.Cell>

                        <Table.Cell className="px-4 py-4 align-top">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-900">
                              {getLoanCompanyName(loan)}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Solicitação de crédito empresarial
                            </p>
                          </div>
                        </Table.Cell>

                        <Table.Cell className="px-4 py-4 text-center align-top">
                          <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-mono text-xs font-semibold tracking-[0.14em] text-slate-700">
                            {getLoanAccessKey(loan)}
                          </span>
                        </Table.Cell>

                        <Table.Cell className="px-4 py-4 text-center align-top">
                          <div className="inline-flex flex-col items-center gap-1">
                            <span className="text-sm font-semibold text-slate-900">
                              {formatLoanDate(loan?.createdAt)}
                            </span>
                            <Badge className="w-fit rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                              {formatLoanAge(loan?.createdAt)}
                            </Badge>
                          </div>
                        </Table.Cell>

                        <Table.Cell className="px-4 py-4 text-center align-top">
                          <div className="inline-flex flex-col items-center">
                            <span className="text-base font-semibold text-emerald-700">
                              {getLoanAmount(loan)}
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400">
                              Crédito
                            </span>
                          </div>
                        </Table.Cell>

                        <Table.Cell className="px-5 py-4 text-right align-top">
                          <SimulationActionButtons
                            loan={loan}
                            onView={handleViewSimulation}
                            onDelete={handleDeleteSimulation}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </div>
          </>
        ) : (
          <EmptyTable icon="search_off">Nenhuma simulação encontrada</EmptyTable>
        )}
      </section>

      {totalPages > 1 && (
        <div className="flex overflow-x-auto justify-center mt-3">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      <Modal
        show={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        size="7xl"
      >
        <ModalBody className="p-0">
          {!selectedLoan || !documentTypes ? (
            <div className="flex min-h-[40vh] items-center justify-center bg-slate-50 px-6 py-10">
              <DrawerLoading />
            </div>
          ) : (
            <div className="max-h-[90vh] overflow-y-auto bg-gray-50 rounded-md">
              <div className="relative overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 px-5 py-5 text-white sm:px-6">
                <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-gray-500/15 blur-3xl" />
                <div className="absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-gray-500/15 blur-3xl" />
                <div className="relative flex flex-col gap-5">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-3xl">
                      <p className="text-[10px] font-bold uppercase tracking-[0.45em] text-white/50">
                        Visualização da simulação
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <span className="flex h-14 w-14 items-center justify-center rounded-3xl bg-white/10 text-white shadow-lg ring-1 ring-white/10">
                          <FaBuilding size={20} />
                        </span>
                        <div className="min-w-0">
                          <h3 className="text-2xl font-semibold sm:text-3xl">
                            {selectedLoan?.Company?.name || "Nome não informado"}
                          </h3>
                          <p className="mt-1 text-sm text-white/60">
                            {selectedLoan?.Company?.document ||
                              "CNPJ não informado"}
                          </p>
                        </div>
                      </div>
                      <p className="mt-4 max-w-2xl text-sm leading-6 text-white/70">
                        Consulte o resumo financeiro, os dados cadastrais e o
                        checklist de documentos enviados nesta simulação.
                      </p>
                    </div>

                    {simulationFormUrl && (
                      <a
                        href={simulationFormUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
                      >
                        <FiExternalLink size={14} />
                        Abrir formulário
                      </a>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <SimulationHeroStatCard
                      label="Código de acesso"
                      value={selectedLoan?.accessKey || "—"}
                      helper="Chave usada no formulário"
                      icon={FiHash}
                      tone="sky"
                      valueClassName="font-mono tracking-[0.14em]"
                    />
                    <SimulationHeroStatCard
                      label="Criada em"
                      value={createdAtDate}
                      helper={createdAtTime}
                      icon={FiCalendar}
                      tone="violet"
                    />
                    <SimulationHeroStatCard
                      label="Valor solicitado"
                      value={requestedAmount}
                      helper={`${selectedLoan?.installments || "—"} parcelas`}
                      icon={FiDollarSign}
                      tone="emerald"
                    />
                    <SimulationHeroStatCard
                      label="Segmento"
                      value={selectedLoan?.SegmentType?.name || "Não informado"}
                      helper={`${companyPartners.length} sócio(s) vinculados`}
                      icon={FiLayers}
                      tone="amber"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-5 px-4 py-4 sm:px-6 sm:py-5">
                <div className="grid gap-5 xl:grid-cols-[1.12fr_0.88fr]">
                  <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.07)]">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
                          <FaBuilding size={18} />
                        </span>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-slate-400">
                            Empresa
                          </p>
                          <h4 className="mt-1 text-lg font-semibold text-slate-900">
                            Dados cadastrais
                          </h4>
                          <p className="mt-1 text-sm text-slate-500">
                            Informações principais da pessoa jurídica.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 p-5 sm:grid-cols-2">
                      <SimulationDetailCard
                        label="CNPJ"
                        value={selectedLoan?.Company?.document || "CNPJ não informado"}
                        icon={FiHash}
                      />
                      <SimulationDetailCard
                        label="Telefone"
                        value={
                          selectedLoan?.Company?.phoneNumber ? (
                            <a
                              target="_blank"
                              rel="noreferrer"
                              className="break-words text-sky-600 transition-colors hover:text-sky-700"
                              href={`https://wa.me/55${selectedLoan?.Company?.phoneNumber?.replace(
                                /\D/g,
                                ""
                              )}`}
                            >
                              {selectedLoan?.Company?.phoneNumber}
                            </a>
                          ) : (
                            "Telefone não informado"
                          )
                        }
                        icon={FaPhone}
                      />
                      <SimulationDetailCard
                        label="E-mail"
                        value={
                          selectedLoan?.Company?.email ? (
                            <a
                              className="break-words text-sky-600 transition-colors hover:text-sky-700"
                              href={`mailto:${selectedLoan?.Company?.email}`}
                            >
                              {selectedLoan?.Company?.email}
                            </a>
                          ) : (
                            "Email não informado"
                          )
                        }
                        icon={FaEnvelope}
                      />
                      <SimulationDetailCard
                        label="Endereço"
                        value={
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-slate-900">
                              {selectedLoan?.Company?.address ||
                                "Endereço não informado"}
                            </p>
                            <p className="text-xs text-slate-500">
                              {selectedLoan?.Company?.addressNeighborhood ||
                                "Bairro não informado"}{" "}
                              ·{" "}
                              {selectedLoan?.Company?.addressCity ||
                                "Cidade não informada"}{" "}
                              · CEP {selectedLoan?.Company?.cep || "—"}
                            </p>
                          </div>
                        }
                        icon={FiMapPin}
                        className="sm:col-span-2"
                      />
                    </div>
                  </section>

                  <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.07)]">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
                          <FiDollarSign size={18} />
                        </span>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-slate-400">
                            Crédito
                          </p>
                          <h4 className="mt-1 text-lg font-semibold text-slate-900">
                            Resumo financeiro
                          </h4>
                          <p className="mt-1 text-sm text-slate-500">
                            Condições principais da proposta.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-5">
                      <div className="rounded-[24px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-lg">
                        <p className="text-[10px] font-bold uppercase tracking-[0.45em] text-white/50">
                          Valor solicitado
                        </p>
                        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                          <div>
                            <p className="text-3xl font-semibold tracking-tight sm:text-4xl">
                              {requestedAmount}
                            </p>
                            <p className="mt-2 text-sm text-white/60">
                              {selectedLoan?.installments || "—"} parcelas
                              solicitadas
                            </p>
                          </div>
                          <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-white/80">
                            {selectedLoan?.SegmentType?.name ||
                              "Segmento não informado"}
                          </span>
                        </div>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
                              Parcelas
                            </p>
                            <p className="mt-2 text-2xl font-semibold">
                              {selectedLoan?.installments || "—"}
                            </p>
                          </div>
                          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
                              Faturamento presumido
                            </p>
                            <p className="mt-2 text-2xl font-semibold">
                              {incomeMonth}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <SimulationDetailCard
                          label="Código de acesso"
                          value={selectedLoan?.accessKey || "—"}
                          icon={FiHash}
                          valueClassName="font-mono tracking-[0.14em]"
                        />
                        <SimulationDetailCard
                          label="Criada em"
                          value={createdAtDate}
                          helper={createdAtTime}
                          icon={FiCalendar}
                        />
                      </div>
                    </div>
                  </section>
                </div>

                <SimulationDocumentsSection
                  title="Documentos da empresa"
                  subtitle="Checklist principal da solicitação."
                  countText={countDocs("company")}
                  icon={FiFileText}
                  docs={companyDocs}
                  emptyText="Nenhum documento configurado para a empresa."
                  getUploadedDoc={getUploadedDoc}
                  openRejectModal={openRejectModal}
                  partnerId={null}
                />

                {companyPartners.length > 0 ? (
                  companyPartners.map((partner) => (
                    <SimulationDocumentsSection
                      key={partner.id}
                      title={`Documentos de ${partner?.name?.toLowerCase()}`}
                      subtitle="Checklist vinculado ao sócio."
                      countText={countDocs("partner", partner.id)}
                      icon={FiFileText}
                      docs={getDocsByAppliesTo("partner", partner.id) || []}
                      emptyText="Nenhum documento configurado para este sócio."
                      getUploadedDoc={getUploadedDoc}
                      openRejectModal={openRejectModal}
                      partnerId={partner.id}
                    />
                  ))
                ) : (
                  <div className="rounded-[28px] border border-dashed border-slate-200 bg-white px-5 py-6 text-center text-sm text-slate-500 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
                    Nenhum sócio cadastrado para esta simulação.
                  </div>
                )}

                <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur sm:px-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-slate-500">
                      Revise os dados e os documentos antes de fechar a
                      visualização.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {simulationFormUrl && (
                        <a
                          href={simulationFormUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                        >
                          <FiExternalLink size={14} />
                          Abrir formulário
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsOpenModal(false)}
                        className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                      >
                        Fechar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        show={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        size="md"
        dismissible
      >
        <ModalHeader>Rejeitar Documento</ModalHeader>
        <ModalBody>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo da rejeição
            </label>
            <select
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            >
              <option value="">Selecione um motivo</option>
              {rejectionOptions.map((reason, i) => (
                <option key={i} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={confirmRejection}>Confirmar Rejeição</Button>
          <Button color="gray" onClick={() => setIsRejectModalOpen(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      <Drawer
        title={`Editar Simulação`}
        onClose={setIsDrawerEditLoan}
        isOpen={isDrawerEditLoan}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <EditQueryCustomForm
          categories={categories}
          onClose={setIsDrawerEditLoan}
          fetchQueries={() => {
            queryClient.invalidateQueries(["loans", currentPage]);
          }}
          query={selectedLoan}
          isOpen={isDrawerEditLoan}
        />
      </Drawer>

      <Drawer
        title={`Criar nova simulação`}
        onClose={setIsDrawerCreateSimulation}
        isOpen={isDrawerCreateSimulation}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <CreateQueryForm
          categories={categories}
          onClose={setIsDrawerCreateSimulation}
          fetchSimulations={() => {
            queryClient.invalidateQueries(["loans", currentPage]);
          }}
          isOpen={isDrawerCreateSimulation}
        />
      </Drawer>
      <ModalDelete
        handleDelete={confirmDeleteSimulation}
        isOpen={isOpenDeleteModal}
        setOpenModal={handleCloseDeleteModal}
        title="Tem certeza que deseja excluir esta simulação?"
        isLoading={isDeletingSimulation}
      />
    </>
  );
};

const HERO_TONES = {
  sky: {
    bar: "bg-sky-400",
    icon: "bg-sky-400/20 text-sky-50",
  },
  violet: {
    bar: "bg-violet-400",
    icon: "bg-violet-400/20 text-violet-50",
  },
  emerald: {
    bar: "bg-emerald-400",
    icon: "bg-emerald-400/20 text-emerald-50",
  },
  amber: {
    bar: "bg-amber-400",
    icon: "bg-amber-400/20 text-amber-50",
  },
};

function SimulationHeroStatCard({
  label,
  value,
  helper,
  icon: Icon,
  tone = "sky",
  valueClassName = "",
}) {
  const colors = HERO_TONES[tone] || HERO_TONES.sky;

  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 shadow-sm backdrop-blur">
      <div className={`h-1 w-full ${colors.bar}`} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-bold uppercase tracking-[0.32em] text-white/50">
              {label}
            </p>
            <p
              className={`mt-2 break-words text-sm font-semibold text-white ${valueClassName}`}
            >
              {value}
            </p>
            {helper && (
              <p className="mt-1 text-[10px] text-white/60">{helper}</p>
            )}
          </div>
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${colors.icon}`}
          >
            <Icon size={16} />
          </span>
        </div>
      </div>
    </div>
  );
}

function SimulationDetailCard({
  label,
  value,
  helper,
  icon: Icon,
  className = "",
  valueClassName = "",
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-100 bg-slate-50/80 p-4 ${className}`}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-900 shadow-sm ring-1 ring-slate-100">
          <Icon size={15} />
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">
            {label}
          </p>
          <div
            className={`mt-1 break-words text-sm font-semibold text-slate-900 ${valueClassName}`}
          >
            {value}
          </div>
          {helper && <p className="mt-1 text-xs text-slate-500">{helper}</p>}
        </div>
      </div>
    </div>
  );
}

function SimulationDocumentItem({ doc, uploadedDoc, onReject }) {
  const isUploaded = Boolean(uploadedDoc);
  const isRejected = Boolean(uploadedDoc?.rejectionReason);

  return (
    <article
      className={`rounded-2xl border p-4 shadow-sm transition ${
        isUploaded
          ? isRejected
            ? "border-rose-100 bg-rose-50/50"
            : "border-emerald-100 bg-emerald-50/40"
          : "border-amber-100 bg-amber-50/40"
      }`}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-start gap-3">
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${
                isUploaded
                  ? isRejected
                    ? "bg-rose-100 text-rose-600"
                    : "bg-emerald-100 text-emerald-600"
                  : "bg-amber-100 text-amber-600"
              }`}
            >
              {isUploaded ? <FaCheck size={12} /> : <FaRegClock size={12} />}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-slate-900">
                  {doc.name}
                </p>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] ${
                    isUploaded
                      ? isRejected
                        ? "bg-rose-100 text-rose-700"
                        : "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {isUploaded
                    ? isRejected
                      ? "Rejeitado"
                      : "Enviado"
                    : "Pendente"}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {isUploaded
                  ? "Arquivo disponível para conferência."
                  : "Aguardando envio do cliente."}
              </p>
            </div>
          </div>

          {isRejected && (
            <p className="mt-3 rounded-xl border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-600">
              Rejeição: {uploadedDoc?.rejectionReason}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isUploaded && (
            <a
              href={uploadedDoc?.fileUrl}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaRegEye size={13} />
              Visualizar documento
            </a>
          )}
          {isUploaded && !isRejected && (
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-600 shadow-sm transition hover:bg-rose-50"
              onClick={onReject}
            >
              <IoMdClose className="text-sm" />
              Rejeitar documento
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function SimulationDocumentsSection({
  title,
  subtitle,
  countText,
  icon: Icon,
  docs = [],
  emptyText,
  getUploadedDoc,
  openRejectModal,
  partnerId = null,
}) {
  const total = docs.length;
  const sent = docs.filter((doc) => doc.uploaded).length;
  const progress = total > 0 ? Math.round((sent / total) * 100) : 0;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.07)]">
      <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
              <Icon size={18} />
            </span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-slate-400">
                Documentos
              </p>
              <h4 className="mt-1 text-lg font-semibold text-slate-900">
                {title}
              </h4>
              {subtitle && (
                <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
              )}
            </div>
          </div>

          <div className="min-w-[180px] sm:text-right">
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
              {countText}
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.24em] text-slate-400">
              {sent} enviados de {total}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-3 p-5">
        {docs.length > 0 ? (
          docs.map((doc) => {
            const uploadedDoc = getUploadedDoc(doc.id, partnerId);

            return (
              <SimulationDocumentItem
                key={partnerId ? `${doc.id}-${partnerId}` : doc.id}
                doc={doc}
                uploadedDoc={uploadedDoc}
                onReject={() => openRejectModal(uploadedDoc?.id)}
              />
            );
          })
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center text-sm text-slate-500">
            {emptyText}
          </div>
        )}
      </div>
    </section>
  );
}

function SimulationActionButtons({ loan, onView, onDelete }) {
  return (
    <div className="flex items-center gap-2 justify-end">
      <button
        type="button"
        title="Visualizar simulação"
        onClick={() => onView(loan)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
      >
        <FaRegEye size={15} />
      </button>
      <button
        type="button"
        title="Excluir simulação"
        onClick={() => onDelete(loan)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-500 shadow-sm transition hover:-translate-y-0.5 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
      >
        <FaRegTrashAlt size={15} />
      </button>
    </div>
  );
}

function SimulationRowCard({ loan, onView, onDelete }) {
  return (
    <article className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
      <div className="h-1 bg-gradient-to-r from-slate-900 via-slate-700 to-slate-400" />
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="w-fit rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-600">
                #{loan?.id}
              </Badge>
              <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                Simulação
              </span>
            </div>
            <h3 className="mt-2 truncate text-sm font-semibold text-slate-900">
              {getLoanCompanyName(loan)}
            </h3>
            <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
              <FaBuilding className="text-[12px] text-slate-400" />
              <span className="truncate">
                Cód. acesso {getLoanAccessKey(loan)}
              </span>
            </div>
          </div>
          <SimulationActionButtons
            loan={loan}
            onView={onView}
            onDelete={onDelete}
          />
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">
              Criação
            </p>
            <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <FaRegClock className="text-[12px] text-slate-400" />
              <span>{formatLoanDate(loan?.createdAt)}</span>
            </div>
            <Badge className="mt-2 w-fit rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500">
              {formatLoanAge(loan?.createdAt)}
            </Badge>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
            <p className="text-[10px] uppercase tracking-[0.24em] text-slate-400">
              Valor
            </p>
            <div className="mt-2 text-lg font-semibold text-emerald-700">
              {getLoanAmount(loan)}
            </div>
            <p className="mt-1 text-xs text-slate-500">Crédito solicitado</p>
          </div>
        </div>
      </div>
    </article>
  );
}

function SimulationSkeletonCard() {
  return (
    <article className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
      <div className="h-1 bg-gradient-to-r from-slate-900 via-slate-700 to-slate-400" />
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-24 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-5 w-3/4 rounded-full bg-slate-200 animate-pulse" />
            <div className="h-3 w-40 rounded-full bg-slate-100 animate-pulse" />
          </div>
          <div className="flex gap-2">
            <div className="h-9 w-9 rounded-xl bg-slate-200 animate-pulse" />
            <div className="h-9 w-9 rounded-xl bg-slate-200 animate-pulse" />
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
            <div className="h-3 w-20 rounded-full bg-slate-200 animate-pulse" />
            <div className="mt-3 h-4 w-28 rounded-full bg-slate-200 animate-pulse" />
            <div className="mt-3 h-5 w-16 rounded-full bg-slate-100 animate-pulse" />
          </div>
          <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
            <div className="h-3 w-16 rounded-full bg-slate-200 animate-pulse" />
            <div className="mt-3 h-6 w-28 rounded-full bg-slate-200 animate-pulse" />
            <div className="mt-3 h-3 w-24 rounded-full bg-slate-100 animate-pulse" />
          </div>
        </div>
      </div>
    </article>
  );
}

export default SimulationsTable;
